<template>
  <BasePage :pageTitle="eventDetails?.eventName || __DICT__.actionMenu.eventAccreditation" back-button :show-menu="true">
    <LoadingSpinner v-if="loading" />

    <div v-else-if="error" class="alert alert-important alert-danger" role="alert">
      <div class="d-flex">
        <div>{{ error }}</div>
      </div>
    </div>

    <div v-else class="d-flex flex-column" style="min-height: calc(100vh - 140px)">
      <!-- Top content section -->
      <div class="flex-grow-1">
        <div class="mb-3">
          <ProgressBar :percent="eventDetails?.entryPercent" :total="eventDetails?.pax" :raw="eventDetails?.entryRaw" :heading="__DICT__.scan?.haveEntered || 'Have Entered'"></ProgressBar>
          <ProgressBar v-if="eventDetails.food" top-margin :percent="(enteredDietary.length/dietary.length) * 100" :total="dietary.length" :raw="enteredDietary.length" :heading="__DICT__.passengerView.dietaryPreferences"></ProgressBar>
          <ProgressBar top-margin :percent="eventDetails?.insidePercent" :total="eventDetails?.pax" :raw="eventDetails?.inside" :heading="__DICT__.scan?.areInside || 'Are Inside'"></ProgressBar>
        </div>

        <h1 style="margin-top: -20px" v-if="showScanner" class="h1">{{ __DICT__.scan?.scanning || 'Scanning' }} {{ direction === 'in' ? (__DICT__.scan?.inLabel || 'In') : (__DICT__.scan?.outLabel || 'Out') }}</h1>
        <QrScannerModal
            v-if="showScanner"
            :modelValue="showScanner"
            @code-scanned="onCodeScanned"
            @invalid-code="onInvalidCode"
            :scanresp="scanresp"
            :scanErr="scanErr"
            :isOnline="isOnline"
            @close="stop"
        />

        <div v-if="scanresp" class="alert alert-important" :class="getAlertClass(scanresp)" role="alert">
          <div class="d-flex">
            <div>
              {{ scanresp.message }}
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span v-if="scanresp.ticket?.bookingRef"> - {{ scanresp.ticket.bookingRef }}</span>
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span v-if="scanresp.ticket?.food === 1 && hasDietaryRequirements(scanresp.ticket?.dietaryRequirements)"><br/>{{ __DICT__.passengerView.dietaryPreferences }}: {{scanresp.ticket?.dietaryRequirements}}</span>
              <div v-if="scanresp.offline" class="mt-1 text-white">
                {{ __DICT__.scan?.offlineNote || '(Offline mode - will sync when connection is restored)' }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="scanErr" class="alert alert-important alert-warning mt-3" role="alert">
          <div class="d-flex">
            <div>
              {{ scanErr.message }}
              <div v-if="scanErr.lastUsed" class="mt-1">
                {{ __DICT__.scan?.lastScanned || 'Last scanned' }}: {{ formatLastUsed(scanErr.lastUsed) }}
              </div>
              <div v-if="!isOnline" class="mt-1 text-muted">
                {{ __DICT__.scan?.offlineCheck || '(Offline mode - please check your connection)' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <NameSearchModal
          :is-open="showNameSearch"
          :tickets="eventDetails?.tickets || []"
          :heading="`${__DICT__.scan?.searchAttendee || 'Search Attendee to'} ${direction === 'in' ? (__DICT__.scan?.enter || 'Enter') : (__DICT__.scan?.exit || 'Exit')}`"
          @close="showNameSearch = false"
          @select="handleNameSelection"
          show-event-status
      />

      <!-- Bottom buttons section -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Direction toggle (always visible) -->
        <SegmentedControl
            v-if="!showScanner"
            :options="directionOptions"
            v-model="direction"
            class="mb-3"
        />

        <!-- Active scanning: stop button -->
        <template v-if="showScanner">
          <button class="btn btn-secondary w-100" @click="stop">
            {{ __DICT__.scan?.stop || 'Stop' }}
          </button>
        </template>

        <!-- Not scanning: scan + name search buttons -->
        <template v-else>
          <button class="btn btn-primary w-100 mb-2" @click="startScanning">
            {{ direction === 'in' ? (__DICT__.scan?.scanIn || 'Scan In') : (__DICT__.scan?.scanOut || 'Scan Out') }}
          </button>
          <button class="btn btn-ghost-secondary w-100" @click="openNameSearch(direction)">
            {{ __DICT__.scan?.searchByName || 'Search by name instead' }}
          </button>
        </template>
      </div>
    </div>
  </BasePage>
</template>

<script setup>
import {ref, watch, onMounted, onUnmounted, computed} from "vue";
import {rememberTicketBookings} from '@/services/qrKeyCache.js';
import { useRoute } from 'vue-router';
import BasePage from "@/components/BasePage.vue";
import { LoadingSpinner, SegmentedControl } from "@/components/atomic";
import { useWeekStore } from '@/stores/week.js'
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import { useHaptics } from "@/composables/useHaptics.js";

const weekStore = useWeekStore()
const { isRecovering } = useEnsureWeekData()
const { successFeedback, errorFeedback } = useHaptics()

import { api } from "@/services/api.js";
import { scanQueue } from "@/services/scanQueue.js";
import { hasDietaryRequirements, formatLastUsed } from '@/utils/scanUtils.js'
import NameSearchModal from "@/components/NameSearchModal.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import QrScannerModal from "@/components/QrScannerModal.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";

const route = useRoute();
const showScanner = ref(false);
const direction = ref('in');
const scanresp = ref(null);
const scanErr = ref(null);
const scanning = ref(false);
const loading = ref(true);
const error = ref(null);
const eventDetails = ref(null);
// The QR check can bind a scan to its ticket's booking (qr-binding-ticket-booking).
watch(() => eventDetails.value?.tickets, rememberTicketBookings);
const showNameSearch = ref(false);
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const weekData = ref([])

const directionOptions = [
  { value: 'in', label: __DICT__.scan?.inLabel || 'In' },
  { value: 'out', label: __DICT__.scan?.outLabel || 'Out' }
]

const startScanning = () => {
  scanresp.value = null;
  scanErr.value = null;
  showScanner.value = true;
}

const stop = () => {
  scanresp.value = null;
  scanErr.value = null;
  showScanner.value = false;
}

const generateGuid = () => {
  const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);

  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

const validateOfflineBarcode = (barcode) => {
  if (!eventDetails.value?.tickets) {
    return { valid: false, ticket: null, message: 'No ticket data available offline' };
  }

  const ticket = eventDetails.value.tickets.find(t => t.barcode === barcode);

  if (!ticket) {
    return {
      valid: false,
      ticket: null,
      message: 'Invalid ticket - not found in offline data'
    };
  }

  if (direction.value === 'in') {
    if (ticket.entryStatus === 'entered') {
      return {
        valid: false,
        ticket,
        message: 'Ticket has already been used to enter'
      };
    }
  } else if (direction.value === 'out') {
    if (ticket.entryStatus !== 'entered') {
      return {
        valid: false,
        ticket,
        message: 'Ticket has not been used to enter yet'
      };
    }
  }

  return {
    valid: true,
    ticket,
    message: `Valid ticket for ${ticket.name}`
  };
};

const getAlertClass = (response) => {
  if (response.offline) {
    return response.valid ? 'alert-info' : 'alert-warning';
  }
  return 'alert-success';
};

const participants = computed(() =>
    weekData.value.filter(passenger =>
        passenger.allocPlaces?.some(place =>
            place.event?.reference.split('-')[2] === route.params.ticketId
        ) &&
        eventDetails.value.tickets.some(ticket => ticket.bookingRef === passenger.reference)
    )
)

const dietary = computed(() => {
  return participants.value.filter(p => hasDietaryRequirements(p.dietaryPreferences))
})

const enteredDietary = computed(() =>
    eventDetails.value.tickets.filter(ticket =>
        dietary.value.some(d => d.reference === ticket.bookingRef) && ticket.entryStatus === 'entered'
    )
)

// Other reps scan the same event, so the tickets (statuses, counts) go stale:
// reloaded on open, after every scan answer, when the name search opens, and
// every minute while the page is open. Once loaded, a reload keeps the page
// as it is (no spinner) and a failed one keeps the last list.
const REFRESH_INTERVAL_MS = 60 * 1000;
let lastFetchedAt = 0;
let fetching = null;
let fetchAgain = false;
let refreshTimer = null;

const fetchEventDetails = () => {
  // One load at a time; asked again mid-load (a scan just landed), load once more after it
  if (fetching) {
    fetchAgain = true;
    return fetching;
  }
  fetching = (async () => {
    const firstLoad = !eventDetails.value;
    try {
      weekData.value = await weekStore.weekData
      if (firstLoad) {
        loading.value = true;
        error.value = null;
      }
      const response = await api.get(`/api/events/info/${route.params.ticketId}`);
      if (response.success) {
        eventDetails.value = response.data;
        lastFetchedAt = Date.now();
      } else {
        throw new Error(response.message || 'Failed to load event details');
      }
    } catch (err) {
      if (firstLoad) {
        error.value = "Failed to load event - this might mean you are offline and have not previously loaded the event.";
      } else {
        console.warn('Failed to refresh event details:', err);
      }
    } finally {
      loading.value = false;
      fetching = null;
    }
    if (fetchAgain) {
      fetchAgain = false;
      await fetchEventDetails();
    }
  })();
  return fetching;
};

const refreshIfStale = () => {
  if (document.visibilityState !== 'visible' || !navigator.onLine || scanning.value) return;
  if (Date.now() - lastFetchedAt < REFRESH_INTERVAL_MS) return;
  fetchEventDetails();
};

const onInvalidCode = (errorMessage) => {
  scanErr.value = {message: errorMessage};
  scanresp.value = null;
  errorFeedback();

  setTimeout(() => {
    if (scanErr.value?.message === errorMessage) {
      scanErr.value = null;
    }
  }, 3000);
};

// A queued scan, shown with the local check against the event as last loaded.
const showSavedOffline = (offlineValidation, data) => {
  scanresp.value = {
    message: offlineValidation.message,
    ticket: offlineValidation.ticket || {bookingRef: data},
    offline: true,
    valid: offlineValidation.valid
  };
  if (offlineValidation.valid) successFeedback(); else errorFeedback();
};

// scan: the scanner's { raw, token, verification } - absent for name search.
const onCodeScanned = async (data, scan = null) => {
  if (scanning.value) return;
  scanning.value = true;
  scanErr.value = null;
  scanresp.value = null;

  if (!weekStore.repObj) {
    scanErr.value = { message: 'Session data not available. Please go back and try again.' };
    scanning.value = false;
    errorFeedback();
    return;
  }

  try {
    const payload = {
      barcode: qrRequestCode(scan, data),
      ...qrRequestFields(scan, data),
      idempotentId: generateGuid(),
      deviceTimestamp: new Date().toISOString(),
      eventId: route.params.ticketId,
      rep: weekStore.repObj.repId
    };

    const endpoint = direction.value === 'in' ? '/api/v3/scan/event-in' : '/api/v3/scan/event-out';
    const localTicket = eventDetails.value?.tickets?.find(t => t.barcode === data);
    const label = localTicket ? `${localTicket.name} (${localTicket.bookingRef})` : data;

    if (!navigator.onLine) {
      const offlineValidation = validateOfflineBarcode(data);

      if (!offlineValidation.valid) {
        scanErr.value = {
          message: offlineValidation.message,
          offline: true
        };
        errorFeedback();
        return;
      }

      await scanQueue.submit(endpoint, payload, label);
      showSavedOffline(offlineValidation, data);
      return;
    }

    const result = await scanQueue.submit(endpoint, payload, label);

    if (result.queued) {
      // Sent but not answered: kept for replay, and checked against the
      // event as last loaded, as if the handset were offline.
      showSavedOffline(validateOfflineBarcode(data), data);
      return;
    }

    scanresp.value = {
      message: result.response.message || (__DICT__.scan?.alreadyRecorded || 'Scan already recorded'),
      ticket: result.response.ticket || localTicket || {bookingRef: data}
    };
    successFeedback();
    fetchEventDetails();

  } catch (error) {
    const errObj = {
      message: error.message || 'Failed to process scan',
      offline: !navigator.onLine
    };
    if (error.lastUsed) {
      errObj.lastUsed = error.lastUsed;
    }
    scanErr.value = errObj;
    errorFeedback();
    // A refusal often means another rep got there first: show the list as it now is
    if (navigator.onLine) fetchEventDetails();
  } finally {
    scanning.value = false;
  }
};

const openNameSearch = (dir) => {
  direction.value = dir;
  showNameSearch.value = true;
  if (navigator.onLine) fetchEventDetails();
};

const handleNameSelection = async (barcode) => {
  showNameSearch.value = false;
  await onCodeScanned(barcode);
};

const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine;
};

onMounted(() => {
  fetchEventDetails();
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  document.addEventListener('visibilitychange', refreshIfStale);
  // Checked often, reloaded only when the last load is a minute old
  refreshTimer = setInterval(refreshIfStale, 10 * 1000);
});

onUnmounted(() => {
  window.removeEventListener('online', updateOnlineStatus);
  window.removeEventListener('offline', updateOnlineStatus);
  document.removeEventListener('visibilitychange', refreshIfStale);
  clearInterval(refreshTimer);
});
</script>
